import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useTranslate } from 'lib/tolgee'
import { useDocumentsManagement } from 'modules/documents/common/contexts/documents-management'
import { useDocumentsModals } from 'modules/documents/common/contexts/documents-modals'
import type { FileErrorTypeName } from 'modules/documents/common/documents.types'

import { MODAL_REFRESH_DELAY } from './modal-documents.consts'
import { translations } from './modal-documents.i18n'
import type { ModalDocumentsModel, ModalDocumentsProps } from './modal-documents.types'
import { dispatchAddFilesEvent, dispatchErrorEvent, dispatchRemoveFileEvent } from './modal-documents.utils'

export const useModalDocumentsModel = ({ isOpen }: Pick<ModalDocumentsProps, 'isOpen'>): ModalDocumentsModel => {
  const { createToast } = useToast()
  const t = useTranslate()
  const { handleDocumentsModalRedirect } = useDocumentsModals()

  const {
    documents,
    isUploading,
    uploadProgress,
    storageInfo,
    uploadDocuments,
    deleteDocument,
    fetchDocuments,
    hasMore,
    isFetchingMore,
    fetchMoreDocuments,
    error,
    isLoading,
    totalCount,
    refreshDocuments,
  } = useDocumentsManagement()

  const [searchQuery, setSearchQuery] = useState('')
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)
  const [isHighlighted, setIsHighlighted] = useState(false)

  const refreshIntervalIdRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // Trigger when modal is open
    fetchDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    // Clear any existing interval first to prevent duplicates
    if (refreshIntervalIdRef.current) {
      clearInterval(refreshIntervalIdRef.current)

      refreshIntervalIdRef.current = null
    }

    // Only create interval when modal is open and not busy
    const shouldRefresh = isOpen && !isUploading && !isFetchingMore && !isLoading

    if (shouldRefresh) {
      refreshIntervalIdRef.current = setInterval(() => {
        refreshDocuments()
      }, MODAL_REFRESH_DELAY)
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (refreshIntervalIdRef.current) {
        clearInterval(refreshIntervalIdRef.current)

        refreshIntervalIdRef.current = null
      }
    }
  }, [isOpen, refreshDocuments, isLoading, isUploading, isFetchingMore])

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents

    const query = searchQuery.toLowerCase()
    return documents.filter(doc => doc.fileName.toLowerCase().includes(query))
  }, [documents, searchQuery])

  const onDragEnter = () => {
    setIsHighlighted(true)
  }

  const onDragLeave = () => {
    setIsHighlighted(false)
  }

  const onSearch = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const onButtonInfoClick = useCallback(handleDocumentsModalRedirect, [handleDocumentsModalRedirect])

  const addFiles = useCallback(
    async (files: File[]) => {
      setIsHighlighted(false)

      await uploadDocuments(files)
    },
    [uploadDocuments]
  )

  const onButtonFilesClick = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)

      dispatchAddFilesEvent('click', fileArray)

      addFiles(fileArray)
    },
    [addFiles]
  )

  const onDropFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)

      dispatchAddFilesEvent('drop', fileArray)

      addFiles(fileArray)
    },
    [addFiles]
  )

  const onDeleteDocument = useCallback((id: string) => {
    setDocumentToDelete(id)
    setShowDeleteConfirmation(true)
  }, [])

  const onError = useCallback(
    (error: FileErrorTypeName) => {
      setIsHighlighted(false)

      dispatchErrorEvent(error)

      if (error === 'file_extension_not_supported') {
        createToast({
          content: t(translations.errorExtensionNotSupported),
        })
      } else if (error === 'max_files_exceeded') {
        createToast({
          content: t(translations.errorMaxFilesExceeded),
        })
      }
    },
    [createToast, t]
  )

  const onConfirmDelete = useCallback(async () => {
    setShowDeleteConfirmation(false)
    if (documentToDelete) {
      dispatchRemoveFileEvent(documentToDelete)

      await deleteDocument(documentToDelete)
    }
    setDocumentToDelete(null)
  }, [documentToDelete, deleteDocument])

  const onCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false)
    setDocumentToDelete(null)
  }, [])

  const onRetryFetch = useCallback(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading || isFetchingMore,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreDocuments,
    disabled: error || searchQuery.trim().length > 0,
  })

  return {
    documents,
    filteredDocuments,
    isUploading,
    uploadProgress,
    storageUsed: storageInfo.used,
    storageLimit: storageInfo.limit,
    showDeleteConfirmation,
    documentToDelete,
    searchQuery,
    error,
    onSearch,
    onButtonFilesClick,
    onDropFiles,
    onDeleteDocument,
    onConfirmDelete,
    onCancelDelete,
    isHighlighted,
    onDragEnter,
    onDragLeave,
    onError,
    onButtonInfoClick,
    onRetryFetch,
    hasMore,
    isFetchingMore,
    sentryRef,
    totalCount,
    isLoading,
  }
}
